/**
 * Admin Page - עמוד ניהול
 * Admin panel with tabs for user management, risk mappings, approvals, and audit logs
 *
 * THREE AUDIENCES, ONE PAGE:
 *   • admin      — everything
 *   • advisor    — everything not marked adminOnly
 *   • sub_admin  — מנהל אזור: ONLY the areas delegated to them, and inside each,
 *                  only content and AI. No users, no audit log, no money, no
 *                  student PII. The server enforces the same list; this just
 *                  avoids rendering tabs that would 403.
 */

import { useMemo, useState } from 'react'
import { useAuthStore } from '../store/authStore'
import { Users, Settings, CheckCircle, FileText, PhoneCall, BookOpen, MessageCircle, Cpu, CreditCard, FileSignature, GraduationCap, Library, BarChart3, ShieldCheck, Scale } from 'lucide-react'
import { TRACKS, PUBLIC_TRACK_KEYS, managedTracks, managesMazan, type TrackKey } from '../config/tracks'
import { Navigate, useSearchParams } from 'react-router-dom'

// Import tab components
import UserManagementTab from '../components/admin/UserManagementTab'
import RiskMappingTab from '../components/admin/RiskMappingTab'
import QuestionnaireApprovalTab from '../components/admin/QuestionnaireApprovalTab'
import AuditLogTab from '../components/admin/AuditLogTab'
import CallbacksTab from '../components/admin/CallbacksTab'
import AdvisorRulesTab from '../components/admin/AdvisorRulesTab'
import FeedbackSummaryTab from '../components/admin/FeedbackSummaryTab'
import ModelSettingsTab from '../components/admin/ModelSettingsTab'
import SubscriptionsAdminTab from '../components/admin/SubscriptionsAdminTab'
import MazanAdminTab from '../components/admin/MazanAdminTab'
import ContractEditTab from '../components/admin/ContractEditTab'
import ClassCodesTab from '../components/admin/ClassCodesTab'
import LearningContentTab from '../components/admin/LearningContentTab'
import ClassProgressTab from '../components/admin/ClassProgressTab'
import TrackSettingsTab from '../components/admin/TrackSettingsTab'
import TrackQuestionsTab from '../components/admin/TrackQuestionsTab'
import SubAdminsTab from '../components/admin/SubAdminsTab'

// Group headers ('student-portal', '<track>-portal') are never an active panel —
// selecting one always resolves to its first sub-tab. The rest map 1:1 to a panel.
//
// Widened to `string` because the per-track sub-tab ids are generated (see
// trackGroups below); a literal union would have to grow by four members for
// every area added, which is exactly the maintenance this feature removes.
type AdminTab = string

interface TabDef {
  id: AdminTab
  label: string
  icon: typeof Users
  component?: () => JSX.Element
  adminOnly?: boolean
  children?: TabDef[]
}

/**
 * The content + AI panels for ONE area. This exact set is what a section admin
 * (sub_admin) is granted, so it is defined once and reused for both audiences —
 * an admin reviewing an area sees the same screens the delegate edits.
 *
 * `component` must be a STABLE reference per tab id (not a fresh arrow each
 * render): the panel below renders <ActiveComponent />, so a new function every
 * render would remount the tab and lose its form state on every keystroke. That is
 * why the whole tree is built inside a useMemo.
 */
function contentTabsFor(key: TrackKey): TabDef[] {
  return [
    {
      id: `${key}-settings`,
      label: 'הגדרות ואישיות ה-AI',
      icon: Cpu,
      component: () => <TrackSettingsTab track={key} />,
    },
    {
      id: `${key}-rules`,
      label: 'כללים ל-AI',
      icon: BookOpen,
      component: () => <AdvisorRulesTab scope={key} />,
    },
    {
      id: `${key}-content`,
      label: 'תוכן',
      icon: Library,
      component: () => <LearningContentTab track={key} />,
    },
    {
      id: `${key}-questions`,
      label: 'שאלות משתמשים',
      icon: MessageCircle,
      component: () => <TrackQuestionsTab track={key} />,
    },
  ]
}

export default function AdminPage() {
  const { user } = useAuthStore()

  const role = user?.role
  const isSectionAdmin = role === 'sub_admin'
  // Stable dependency for the memo below — `managedTracks` returns a new array.
  const myTrackKeys = managedTracks(user).join(',')
  const runsMazan = managesMazan(user)

  const availableTabs = useMemo<TabDef[]>(() => {
    /**
     * A section admin's whole panel: one group per area they run, nothing else.
     * The student area is included here the same way the public ones are — a
     * delegate who runs it edits its lessons and its AI, but never its class
     * codes or the progress report, both of which carry student PII.
     */
    if (isSectionAdmin) {
      const groups: TabDef[] = (myTrackKeys ? (myTrackKeys.split(',') as TrackKey[]) : []).map((key) => ({
        id: `${key}-portal`,
        label: TRACKS[key].name,
        icon: TRACKS[key].Icon,
        children: contentTabsFor(key),
      }))
      // מנהל אזור of מאזן: price, coupons, confirming customers — never the reports
      // (the tab renders its manager view; the API enforces the same line).
      if (runsMazan) groups.push({ id: 'mazan', label: 'איזון', icon: Scale, component: () => <MazanAdminTab /> })
      return groups
    }

    // Everything about the school programme lives behind one entry, so the top
    // bar doesn't grow six near-identical student tabs.
    const studentPortal: TabDef = {
      id: 'student-portal',
      label: 'פורטל תלמידים',
      icon: GraduationCap,
      adminOnly: true,
      children: [
        { id: 'class-codes', label: 'כיתות ותלמידים', icon: GraduationCap, component: () => <ClassCodesTab /> },
        { id: 'class-progress', label: 'התקדמות כיתות', icon: BarChart3, component: () => <ClassProgressTab /> },
        ...contentTabsFor('student'),
      ],
    }

    // תכנון פרישה, משכנתא — same shape as the student portal group, minus the
    // class-code and progress screens, which only the school programme has.
    const trackGroups: TabDef[] = PUBLIC_TRACK_KEYS.map((key) => ({
      id: `${key}-portal`,
      label: TRACKS[key].name,
      icon: TRACKS[key].Icon,
      adminOnly: true,
      children: contentTabsFor(key),
    }))

    const all: TabDef[] = [
      { id: 'users', label: 'משתמשים', icon: Users, component: () => <UserManagementTab /> },
      { id: 'risk-mappings', label: 'מיפוי סיכון', icon: Settings, component: () => <RiskMappingTab /> },
      { id: 'approvals', label: 'אישור שאלונים', icon: CheckCircle, component: () => <QuestionnaireApprovalTab /> },
      { id: 'callbacks', label: 'בקשות חזרה', icon: PhoneCall, component: () => <CallbacksTab /> },
      { id: 'advisor-rules', label: 'כללי יועצי', icon: BookOpen, component: () => <AdvisorRulesTab /> },
      { id: 'feedback-summary', label: 'סיכום משובים', icon: MessageCircle, component: () => <FeedbackSummaryTab /> },
      { id: 'subscriptions', label: 'מנויים', icon: CreditCard, component: () => <SubscriptionsAdminTab />, adminOnly: true },
      // מאזן reports hold divorce-case PII — full admins only (the API enforces it too).
      { id: 'mazan', label: 'איזון', icon: Scale, component: () => <MazanAdminTab />, adminOnly: true },
      { id: 'model-settings', label: 'הגדרות מודל', icon: Cpu, component: () => <ModelSettingsTab />, adminOnly: true },
      studentPortal,
      ...trackGroups,
      { id: 'sub-admins', label: 'מנהלי אזור', icon: ShieldCheck, component: () => <SubAdminsTab />, adminOnly: true },
      { id: 'contract', label: 'עריכת חוזה', icon: FileSignature, component: () => <ContractEditTab />, adminOnly: true },
      { id: 'audit-logs', label: 'יומן ביקורת', icon: FileText, component: () => <AuditLogTab />, adminOnly: true },
    ]

    return all.filter((tab) => !tab.adminOnly || role === 'admin')
  }, [isSectionAdmin, myTrackKeys, runsMazan, role])

  /**
   * The landing tab. Not hard-coded to 'users': a section admin has no such tab,
   * and rendering a panel that doesn't exist would show them an empty page.
   */
  const firstTabId = availableTabs[0]?.children?.[0]?.id ?? availableTabs[0]?.id ?? ''
  // ?tab=<id> deep link (e.g. the מאזן purchase email) — honoured only for a tab
  // this user actually has.
  const [searchParams] = useSearchParams()
  const wantedTab = searchParams.get('tab')
  const reachable = availableTabs.flatMap((t) => (t.children ? t.children.map((c) => c.id) : [t.id]))
  const [activeTab, setActiveTab] = useState<AdminTab>(
    wantedTab && reachable.includes(wantedTab) ? wantedTab : firstTabId
  )

  // Check if user has a role that reaches the admin panel
  if (!user || (user.role !== 'admin' && user.role !== 'advisor' && user.role !== 'sub_admin')) {
    return <Navigate to="/dashboard" replace />
  }

  // A section admin whose areas were all revoked while they were signed in.
  if (availableTabs.length === 0) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center">
        <h1 className="text-2xl font-heading font-bold text-gray-900 mb-2">פאנל ניהול</h1>
        <p className="text-gray-600">לא הוקצו לך אזורים לניהול. אנא פנה/י למנהל המערכת.</p>
      </div>
    )
  }

  /** A grouped tab is "selected" whenever any of its sub-tabs is the active one. */
  const isTabActive = (tab: TabDef) =>
    tab.children ? tab.children.some((child) => child.id === activeTab) : activeTab === tab.id

  const activeParent = availableTabs.find((tab) => tab.children && isTabActive(tab))

  // Falls back to the first tab so a stale selection — a section admin whose area
  // was reassigned mid-session — shows a panel rather than an empty page.
  const ActiveComponent =
    availableTabs.find((tab) => !tab.children && tab.id === activeTab)?.component ??
    activeParent?.children?.find((child) => child.id === activeTab)?.component ??
    availableTabs[0]?.children?.[0]?.component ??
    availableTabs[0]?.component

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold text-gray-900 mb-2">
          פאנל ניהול
        </h1>
        <p className="text-gray-600">
          {isSectionAdmin
            ? `ניהול תוכן ו-AI באזורים: ${availableTabs.map((t) => t.label).join(', ')}`
            : user.role === 'admin'
            ? 'ניהול מערכת'
            : 'ניהול משתמשים והמלצות'}
        </p>
      </div>

      {/* Tabs Navigation — wraps to multiple rows so every option stays visible */}
      <div className="border-b border-gray-200 pb-3 mb-6">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="לשוניות ניהול">
          {availableTabs.map((tab) => {
            const Icon = tab.icon
            const isActive = isTabActive(tab)

            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                // Opening a group lands on its first sub-tab.
                onClick={() => setActiveTab(tab.children ? tab.children[0].id : tab.id)}
                className={`
                  flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1
                  ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-200'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }
                `}
              >
                <Icon size={18} aria-hidden="true" />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </div>

        {/* Sub-tabs of the open group, e.g. פורטל תלמידים */}
        {activeParent?.children && (
          <div
            className="flex flex-wrap gap-2 mt-3 ps-1 border-t border-gray-100 pt-3"
            role="tablist"
            aria-label={`תת-לשוניות ${activeParent.label}`}
          >
            {activeParent.children.map((child) => {
              const ChildIcon = child.icon
              const isChildActive = activeTab === child.id

              return (
                <button
                  key={child.id}
                  role="tab"
                  aria-selected={isChildActive}
                  onClick={() => setActiveTab(child.id)}
                  className={`
                    flex items-center gap-2 rounded-full px-3 py-1.5 text-sm transition-colors whitespace-nowrap
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1
                    ${
                      isChildActive
                        ? 'bg-primary-600 text-white font-medium'
                        : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                    }
                  `}
                >
                  <ChildIcon size={16} aria-hidden="true" />
                  <span>{child.label}</span>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Tab Content */}
      <div role="tabpanel" className="min-h-[600px]">
        {ActiveComponent ? <ActiveComponent /> : null}
      </div>
    </div>
  )
}
